import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, LogOut, RefreshCw, Lock, AlertCircle, Mail, User } from "lucide-react";
import { LOGO_LIGHT } from "@/lib/images";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "yz_admin_token";

// ---------- Hooks ----------
const useAdminData = (token) => {
  const [stats, setStats] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const tok = encodeURIComponent(token);
      const [s, w, i] = await Promise.all([
        axios.get(`${API}/admin/stats?token=${tok}`),
        axios.get(`${API}/waitlist?token=${tok}`),
        axios.get(`${API}/investors?token=${tok}`),
      ]);
      setStats(s.data);
      setWaitlist(w.data);
      setInvestors(i.data);
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Invalid token.");
      } else {
        setError("Could not load. Please try again.");
      }
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { stats, waitlist, investors, loading, error, load, setError };
};

// ---------- Components ----------
const Stat = ({ label, value }) => (
  <div className="flex-1 border border-ivory/15 px-8 py-7" data-testid={`stat-${label.toLowerCase()}`}>
    <div className="overline text-bronze mb-3">{label}</div>
    <div className="font-serif text-5xl font-light text-ivory">{value ?? "—"}</div>
  </div>
);

const Table = ({ title, columns, rows, csvUrl, testid }) => (
  <section className="mt-16" data-testid={testid}>
    <div className="flex items-end justify-between mb-6 border-b border-ivory/15 pb-4">
      <div>
        <p className="overline text-bronze mb-2">{title}</p>
        <h2 className="font-serif text-3xl md:text-4xl font-light text-ivory">
          {rows.length} {rows.length === 1 ? "entry" : "entries"}
        </h2>
      </div>
      <a
        href={csvUrl}
        download
        data-testid={`${testid}-csv-download`}
        className="btn-bronze overline px-6 py-3 flex items-center gap-2"
      >
        <Download size={14} /> Download CSV
      </a>
    </div>
    {rows.length === 0 ? (
      <p className="text-ivory/55 text-sm py-10">No submissions yet.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              {columns.map((c) => (
                <th key={c.key} className="overline text-ivory/55 py-3 pr-6 font-medium align-bottom">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-ivory/10 hover:bg-ivory/[0.03]">
                {columns.map((c) => (
                  <td key={c.key} className="py-4 pr-6 text-ivory/90 align-top">
                    {c.render ? c.render(r[c.key], r) : String(r[c.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

// ---------- Login Card ----------
const Login = ({ onSubmit, error }) => {
  const [val, setVal] = useState("");
  return (
    <div className="min-h-screen flex items-center justify-center bg-forest text-ivory px-6" data-testid="admin-login">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(val);
        }}
        className="w-full max-w-md border border-ivory/15 p-10 md:p-12"
      >
        <div className="flex items-center gap-3 mb-10">
          <img src={LOGO_LIGHT} alt="Yuzenya" className="h-12 w-12 object-contain" />
          <span className="font-serif text-2xl tracking-[0.2em] text-ivory">YUZENYA</span>
        </div>
        <p className="overline text-bronze mb-4">Admin Access</p>
        <h1 className="font-serif text-4xl font-light leading-tight mb-10">
          Sign in to view leads
        </h1>
        <label className="overline text-ivory/65 block mb-2">Admin token</label>
        <input
          type="password"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="••••••••••••"
          autoFocus
          data-testid="admin-token-input"
          className="input-line input-line-dark mb-4"
        />
        {error && (
          <p className="overline text-bronze flex items-center gap-2 mb-4" data-testid="admin-login-error">
            <AlertCircle size={14} /> {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit"
          className="btn-bronze overline px-8 py-4 mt-4 inline-flex items-center gap-2"
        >
          <Lock size={14} /> Enter
        </button>
        <p className="overline text-ivory/45 mt-10 leading-relaxed">
          Token is sent only to <code className="text-ivory/70">/api/admin/*</code> over HTTPS.
        </p>
      </form>
    </div>
  );
};

// ---------- Dashboard ----------
export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [loginError, setLoginError] = useState("");
  const { stats, waitlist, investors, loading, error, load } = useAdminData(token);

  useEffect(() => {
    if (token) {
      load().catch(() => {
        // invalid token — drop it
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setLoginError("Invalid token.");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onLogin = (val) => {
    const trimmed = (val || "").trim();
    if (!trimmed) {
      setLoginError("Please enter the admin token.");
      return;
    }
    sessionStorage.setItem(TOKEN_KEY, trimmed);
    setLoginError("");
    setToken(trimmed);
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLoginError("");
  };

  const csv = useMemo(
    () => ({
      waitlist: `${API}/admin/export/waitlist.csv?token=${encodeURIComponent(token)}`,
      investors: `${API}/admin/export/investors.csv?token=${encodeURIComponent(token)}`,
    }),
    [token],
  );

  if (!token) {
    return <Login onSubmit={onLogin} error={loginError} />;
  }

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-forest text-ivory" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-ivory/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_LIGHT} alt="Yuzenya" className="h-10 w-10 object-contain" />
            <div>
              <p className="overline text-bronze">Yuzenya — Admin</p>
              <p className="font-serif text-xl">Leads & Inquiries</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={load}
              disabled={loading}
              className="overline text-ivory/70 hover:text-ivory flex items-center gap-2 disabled:opacity-50"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="overline text-ivory/70 hover:text-ivory flex items-center gap-2"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        {error && (
          <p className="overline text-bronze flex items-center gap-2 mb-8" data-testid="admin-error">
            <AlertCircle size={14} /> {error}
          </p>
        )}

        {/* Stats */}
        <div className="flex flex-col md:flex-row gap-6" data-testid="admin-stats-grid">
          <Stat label="Waiting list" value={stats?.waitlist} />
          <Stat label="Investor inquiries" value={stats?.investors} />
          <Stat
            label="Total leads"
            value={
              stats ? (stats.waitlist || 0) + (stats.investors || 0) : null
            }
          />
        </div>

        <Table
          title="Waiting list"
          testid="waitlist-table"
          csvUrl={csv.waitlist}
          columns={[
            {
              key: "first_name",
              label: "Name",
              render: (v) => (
                <span className="flex items-center gap-2">
                  <User size={14} className="text-ivory/45" /> {v || "—"}
                </span>
              ),
            },
            {
              key: "email",
              label: "Email",
              render: (v) => (
                <a href={`mailto:${v}`} className="flex items-center gap-2 hover:text-bronze">
                  <Mail size={14} className="text-ivory/45" /> {v}
                </a>
              ),
            },
            { key: "source", label: "Source", render: (v) => <span className="overline text-ivory/65">{v}</span> },
            { key: "locale", label: "Lang", render: (v) => <span className="overline text-ivory/65">{(v || "en").toUpperCase()}</span> },
            { key: "created_at", label: "Joined", render: fmtDate },
          ]}
          rows={waitlist}
        />

        <Table
          title="Investor inquiries"
          testid="investors-table"
          csvUrl={csv.investors}
          columns={[
            {
              key: "name",
              label: "Name",
              render: (v) => (
                <span className="flex items-center gap-2">
                  <User size={14} className="text-ivory/45" /> {v}
                </span>
              ),
            },
            {
              key: "email",
              label: "Email",
              render: (v) => (
                <a href={`mailto:${v}`} className="flex items-center gap-2 hover:text-bronze">
                  <Mail size={14} className="text-ivory/45" /> {v}
                </a>
              ),
            },
            {
              key: "interest",
              label: "Interest",
              render: (v) => (
                <span className="text-ivory/85 line-clamp-3 max-w-md inline-block">{v}</span>
              ),
            },
            { key: "locale", label: "Lang", render: (v) => <span className="overline text-ivory/65">{(v || "en").toUpperCase()}</span> },
            { key: "created_at", label: "Submitted", render: fmtDate },
          ]}
          rows={investors}
        />
      </main>
    </div>
  );
}
